import type { LookupItem } from '../mocks/criminalMasterData';

const COURT_TYPE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCourtTypeUuid(value: string): boolean {
  return COURT_TYPE_UUID_RE.test(value.trim());
}

/** Parse /court-types rows — lookup id must be the master UUID, never a short code. */
export function parseCourtTypesFromApi(data: unknown): LookupItem[] {
  if (!Array.isArray(data)) return [];
  const rows: LookupItem[] = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const id = typeof r.id === 'string' ? r.id.trim() : '';
    if (!name || !id || !isCourtTypeUuid(id)) continue;
    const code = typeof r.code === 'string' ? r.code.trim() : undefined;
    rows.push({ id, name, code: code || undefined });
  }
  return rows;
}

export function findCourtTypeRow(
  courtTypes: LookupItem[],
  courtTypeIdOrCode: string,
): LookupItem | undefined {
  const key = courtTypeIdOrCode.trim();
  if (!key) return undefined;
  const byId = courtTypes.find((c) => c.id === key);
  if (byId) return byId;
  const upper = key.toUpperCase();
  return courtTypes.find((c) => (c.code ?? '').trim().toUpperCase() === upper);
}

/** Resolve draft/API court type value to the master UUID for save payloads. */
export function resolveCourtTypeIdForApi(
  courtTypes: LookupItem[],
  courtTypeId: string,
  courtTypeName?: string,
): string {
  const raw = courtTypeId.trim();
  if (!raw) return '';
  if (isCourtTypeUuid(raw)) return raw;

  const row = findCourtTypeRow(courtTypes, raw);
  if (row) return row.id;

  const name = (courtTypeName ?? '').trim().toLowerCase();
  if (name) {
    const byName = courtTypes.find((c) => c.name.trim().toLowerCase() === name);
    if (byName) return byName.id;
  }

  return raw;
}

export function resolveDraftCourtTypeFields(
  courtTypes: LookupItem[],
  fields: { courtTypeId: string; courtTypeName: string },
): { courtTypeId: string; courtTypeName: string } {
  const courtTypeId = resolveCourtTypeIdForApi(courtTypes, fields.courtTypeId, fields.courtTypeName);
  const row = findCourtTypeRow(courtTypes, courtTypeId);
  return {
    courtTypeId,
    courtTypeName: row?.name ?? fields.courtTypeName,
  };
}

/** CMS-MC-1 courts query param — prefer court type code over UUID. */
export function courtTypeQueryParam(courtTypes: LookupItem[], courtTypeId: string): string {
  const id = courtTypeId.trim();
  if (!id) return '';
  const row = findCourtTypeRow(courtTypes, id);
  if (row?.code?.trim()) return row.code.trim();
  return id;
}
